<template>
  <template v-if="meterpoint.isActive && getLatestTariff?.productDetails.id">
    <div
      v-for="(agreement, index) in reversedActiveAgreements"
      :key="agreement.productDetails.id"
      :class="
        getActiveAgreements && index !== getActiveAgreements?.length - 1
          ? 'mb-3'
          : ''
      "
    >
      <div class="d-flex align-center mb-2 gap-1 letter-spacing-0">
        <h5 class="font-weight-light">
          {{ agreement?.productDetails.displayName }}
        </h5>
        <v-spacer />
        <SoStatus :status="agreement.status" />
      </div>

      <p>{{ formatDateRange(agreement) }}</p>

      <p
        v-if="agreement.productDetails.requiresSmartCharging"
        class="mt-3 d-flex align-center gap-2"
        data-testid="requires-smart-charging"
      >
        <SoIcon icon="electric" class="pa-1 bg-border rounded" />
        Requires Smart Charging
      </p>
    </div>
    <p
      v-if="hasFlexMarketSubscription"
      class="mt-3 d-flex align-center gap-2"
      data-testid="flex-market-subscription"
    >
      <SoIcon icon="car" class="pa-1 bg-border rounded" />
      So Charged subscription
    </p>
  </template>
  <div v-else class="mt-2 pa-4 h-100 text-center bg-surface-dim">
    <i class="text-medium-emphasis">No Active Agreement</i>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoStatus from "@/components/SoStatus.vue"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { formatDate } from "@/helpers/dateHelper"
  import { cloneDeep } from "lodash-es"
  import { IMeterPointAgreement } from "@/models/meterpointAgreement/meterpointAgreement.types"
  import { FlexMarketSubscriptionService } from "@/services/FlexMarketSubscriptionService"
  import SoIcon from "@/components/SoIcon.vue"

  export default defineComponent({
    components: {
      SoStatus,
      SoIcon,
    },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        default: null,
        required: true,
      },
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        formatDate,
      }
    },
    computed: {
      getActiveAgreements() {
        if (
          this.current.coreId.data === null ||
          !this.current.agreementsByMeterPoint.data[this.meterpoint.identifier]
        )
          return null
        let agreementsCopy = [
          ...this.current.agreementsByMeterPoint.data[
            this.meterpoint.identifier
          ],
        ]
        agreementsCopy = agreementsCopy.filter(
          (agreement) =>
            agreement.toDate == null ||
            (agreement.toDate &&
              new Date(agreement.toDate?.format()) > new Date()),
        )
        const meterpointAgreements = agreementsCopy.sort((a, b) => {
          if (a.toDate == null) {
            return -1
          }
          if (b.toDate == null) {
            return 1
          }
          return a.toDate.isAfter(b.toDate) ? -1 : 1
        })
        return meterpointAgreements
      },
      reversedActiveAgreements() {
        return cloneDeep(this.getActiveAgreements)?.reverse()
      },
      getLatestTariff() {
        return this.getActiveAgreements?.[0]
      },

      flexMarketSubscriptions() {
        const billingAccountId = this.current.coreId.data
        if (!billingAccountId) return null
        return FlexMarketSubscriptionService.getForAccount(billingAccountId)
      },

      hasFlexMarketSubscription() {
        return !!this.flexMarketSubscriptions?.data?.some(
          (s) =>
            s.isActive && s.meterpointIdentifier === this.meterpoint.identifier,
        )
      },
    },
    methods: {
      formatDateRange(agreement: IMeterPointAgreement) {
        return (
          agreement &&
          `${this.formatDate(agreement?.fromDate)}${
            !agreement.toDate
              ? " onwards"
              : " - " + this.formatDate(agreement?.toDate)
          }`
        )
      },
    },
  })
</script>

<style scoped>
  .letter-spacing-0 {
    letter-spacing: 0;
  }
</style>
